import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { useTagMutation } from 'utils/api/mutations';
import { toastStyle } from 'utils/helper';
import { Icon } from 'utils/icons';

import './styles.scss';

const TAG_COLORS = [
  '#2563EB',
  '#7C3AED',
  '#DB2777',
  '#DC2626',
  '#EA580C',
  '#CA8A04',
  '#16A34A',
  '#0891B2',
];

const normalizeTagName = value =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 40);

const normalizeTagId = value => {
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
};

const randomTagColor = () =>
  TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)];

const getErrorMessage = error => {
  const data = error?.response?.data;
  if (!data) return error?.message || 'Failed to create tag.';
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  if (data.non_field_errors?.[0]) return data.non_field_errors[0];
  const firstKey = Object.keys(data)[0];
  const firstValue = firstKey ? data[firstKey] : null;
  if (Array.isArray(firstValue)) return firstValue[0];
  if (typeof firstValue === 'string') return firstValue;
  return 'Failed to create tag.';
};

const QualifiedSpendTagPicker = ({
  budgetId,
  tags,
  selectedTagIds,
  onChange,
  disabled,
}) => {
  const queryClient = useQueryClient();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [createdTags, setCreatedTags] = useState([]);
  const createTagMutation = useTagMutation.useCreateTagMutation();
  const isCreating = createTagMutation.isLoading || createTagMutation.isPending;

  useEffect(() => {
    setCreatedTags([]);
    setQuery('');
    setIsOpen(false);
  }, [budgetId]);

  useEffect(() => {
    const handlePointerDown = event => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const options = useMemo(() => {
    const seen = new Set();
    return [...(tags || []), ...createdTags].filter(tag => {
      const id = normalizeTagId(tag?.id);
      const name = normalizeTagName(tag?.name);
      if (id === null || !name || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [createdTags, tags]);

  const selectedIds = useMemo(
    () => (selectedTagIds || []).map(normalizeTagId).filter(id => id !== null),
    [selectedTagIds]
  );
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedTags = options.filter(tag =>
    selectedIdSet.has(normalizeTagId(tag.id))
  );
  const queryText = normalizeTagName(query);
  const matchingOption = options.find(
    tag => tag.name.toLowerCase() === queryText.toLowerCase()
  );
  const filteredOptions = options.filter(tag => {
    const tagId = normalizeTagId(tag.id);
    return (
      !selectedIdSet.has(tagId) &&
      tag.name.toLowerCase().includes(queryText.toLowerCase())
    );
  });
  const canCreate = Boolean(queryText) && !matchingOption;

  const toggleTag = tag => {
    if (disabled || isCreating) return;
    const tagId = normalizeTagId(tag.id);
    if (tagId === null) return;
    onChange(
      selectedIdSet.has(tagId)
        ? selectedIds.filter(id => id !== tagId)
        : [...selectedIds, tagId]
    );
    setQuery('');
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const createTag = async () => {
    if (!queryText || disabled || isCreating || !budgetId) return;
    if (matchingOption) {
      toggleTag(matchingOption);
      return;
    }

    const color = randomTagColor();
    try {
      const response = await createTagMutation.mutateAsync({
        budget: Number(budgetId),
        name: queryText,
        color,
      });
      const created = response?.data || response;
      const createdId = normalizeTagId(created?.id);
      if (createdId === null) {
        throw new Error('The new tag could not be selected.');
      }
      const nextTag = {
        ...created,
        id: createdId,
        name: normalizeTagName(created?.name || queryText),
        color: created?.color || color,
      };
      setCreatedTags(current => [...current, nextTag]);
      onChange(
        selectedIdSet.has(createdId) ? selectedIds : [...selectedIds, createdId]
      );
      setQuery('');
      setIsOpen(true);
      await queryClient.invalidateQueries({ queryKey: ['budget-tags'] });
      toast.success(`Tag “${nextTag.name}” created and selected.`);
      inputRef.current?.focus();
    } catch (error) {
      toast.error(getErrorMessage(error), toastStyle);
    }
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (matchingOption) toggleTag(matchingOption);
      else if (canCreate) createTag();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
      setQuery('');
      inputRef.current?.blur();
    }
  };

  return (
    <div className="qualified-tag-picker" ref={rootRef}>
      <div
        className="qualified-tag-picker__surface"
        onClick={() => {
          if (disabled || isCreating) return;
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        {selectedTags.map(tag => (
          <span className="qualified-tag-picker__chip" key={tag.id}>
            <span
              className="qualified-tag-picker__swatch"
              style={{ backgroundColor: tag.color || '#6B7280' }}
            />
            <span>{tag.name}</span>
            <button
              type="button"
              aria-label={`Remove ${tag.name}`}
              onClick={event => {
                event.stopPropagation();
                toggleTag(tag);
              }}
              disabled={disabled || isCreating}
            >
              <Icon name="CloseIcon" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={query}
          onChange={event => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (!isOpen) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={
            selectedTags.length ? 'Add another tag' : 'Search or create a tag'
          }
          aria-label="Search or create qualified spend tag"
          maxLength={40}
          autoComplete="off"
          disabled={disabled || isCreating}
        />
      </div>

      {isOpen && !disabled && (
        <div className="qualified-tag-picker__menu" role="listbox">
          {filteredOptions.map(tag => (
            <button
              type="button"
              className="qualified-tag-picker__option"
              role="option"
              aria-selected={false}
              key={tag.id}
              onMouseDown={event => event.preventDefault()}
              onClick={() => toggleTag(tag)}
            >
              <span
                className="qualified-tag-picker__swatch"
                style={{ backgroundColor: tag.color || '#6B7280' }}
              />
              <span>{tag.name}</span>
            </button>
          ))}

          {canCreate && (
            <button
              type="button"
              className="qualified-tag-picker__option qualified-tag-picker__create"
              onMouseDown={event => event.preventDefault()}
              onClick={createTag}
              disabled={isCreating}
            >
              <Icon name="AddIcon" />
              <span>Create New Tag: {queryText}</span>
            </button>
          )}

          {!filteredOptions.length && !canCreate && (
            <div className="qualified-tag-picker__empty">
              No available tags. Type a name to create one.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

QualifiedSpendTagPicker.propTypes = {
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  tags: PropTypes.arrayOf(PropTypes.object),
  selectedTagIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

QualifiedSpendTagPicker.defaultProps = {
  tags: [],
  selectedTagIds: [],
  disabled: false,
};

export default QualifiedSpendTagPicker;
